import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { reportsApi } from '../../services/reportsApi';

export function SafetyCaseOwnerAssignDialog({
  open,
  safetyCaseId,
  currentOwner,
  onClose,
  onSaved,
}: {
  open: boolean;
  safetyCaseId: string;
  currentOwner?: string | null;
  onClose: () => void;
  onSaved: (next: { owner: string | null; isAssigned: boolean }) => void;
}) {
  const assigned = Boolean(String(currentOwner || '').trim());
  const [owner, setOwner] = useState(String(currentOwner || '').trim());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setOwner(String(currentOwner || '').trim());
    setError(null);
    setBusy(false);
  }, [currentOwner, open]);

  const save = async (clear: boolean) => {
    const nextOwner = clear ? '' : owner.trim();
    if (!clear && !nextOwner) {
      setError('Assign a Case owner before creating this Safety Case.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await reportsApi.patchSafetyCaseOwner(
        safetyCaseId,
        clear ? { unassigned: true } : { owner: nextOwner },
      );
      onSaved({
        owner: result.owner ?? null,
        isAssigned: Boolean(result.is_assigned),
      });
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Unable to update Safety Case owner.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      data-testid="safety-case-owner-assign-dialog"
    >
      <DialogTitle>{assigned ? 'Change owner' : 'Assign owner'}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Owner belongs to this Safety Case. Control assignee and pattern owner stay separate.
          </Typography>
          <TextField
            autoFocus
            label="Owner"
            value={owner}
            onChange={(event) => setOwner(event.target.value)}
            placeholder="ops-lead@site.com"
            disabled={busy}
            inputProps={{ 'data-testid': 'safety-case-owner-input' }}
          />
          {error ? (
            <Typography variant="body2" color="error" data-testid="safety-case-owner-error">
              {error}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => { void save(false); }}
            disabled={busy}
            data-testid="safety-case-owner-save"
          >
            {assigned ? 'Save owner' : 'Assign owner'}
          </Button>
        </DialogActions>
    </Dialog>
  );
}
